<template>
  <tr class="tank-charge-row" :class="{ 'charge-error-row': hasFuelableError }">
    <td colspan="5" class="tank-charge-cell">
      <div class="tank-charge-single">
        <div class="charge-field">
          <label class="charge-label">{{ $t("FORM_LABELS.DATE") }}</label>
          <v-menu
            v-model="localDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="charge.dateCharged"
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="charge-input"
              ></v-text-field>
            </template>
            <v-date-picker
              :value="charge.dateCharged"
              @input="updateDate"
            ></v-date-picker>
          </v-menu>
        </div>

        <div class="charge-field charge-field-wide">
          <label
            class="charge-label"
            :class="{ 'label-error': hasFuelableError }"
          >
            {{ $t("MENU.ITEM.TANKS.VEHICLE_GAS_STATION") }}
            <v-icon v-if="hasFuelableError" x-small color="error"
              >mdi-alert-circle</v-icon
            >
          </label>
          <v-autocomplete
            :value="charge.fuelable"
            :items="vehicles"
            item-text="name"
            return-object
            dense
            hide-details
            class="charge-input"
            :class="{ 'field-with-error': hasFuelableError }"
            @input="$emit('update-field', 'fuelable', $event)"
          ></v-autocomplete>
          <span v-if="hasFuelableError" class="field-error-text">
            {{ $t("LABELS.REQUIRED") }}
          </span>
        </div>

        <div class="charge-field charge-field-wide">
          <label class="charge-label">{{ $t("MENU.ITEM.STAFF.STAFF") }}</label>
          <v-autocomplete
            :value="charge.staffId"
            :items="staffOptions"
            item-text="name"
            item-value="id"
            dense
            hide-details
            clearable
            class="charge-input"
            @input="$emit('update-field', 'staffId', $event)"
          ></v-autocomplete>
        </div>

        <div class="charge-field">
          <label class="charge-label">{{
            $t("MENU.ITEM.FUELS.MILEAGES")
          }}</label>
          <v-text-field
            :value="charge.mileage"
            type="number"
            dense
            hide-details
            :suffix="$t('LABELS.KM')"
            class="charge-input"
            @input="$emit('update-field', 'mileage', parseFloat($event) || 0)"
          ></v-text-field>
        </div>

        <div class="charge-field">
          <label class="charge-label">{{ $t("FORM_LABELS.QTY") }}</label>
          <v-text-field
            :value="charge.liters"
            type="number"
            step="0.01"
            dense
            hide-details
            :suffix="$t('LABELS.LITERS')"
            class="charge-input"
            @input="$emit('update-field', 'liters', parseFloat($event) || 0)"
          ></v-text-field>
        </div>

        <div class="charge-field charge-actions-field">
          <label class="charge-label">&nbsp;</label>
          <v-btn icon x-small color="error" @click="$emit('delete')">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
export default {
  name: "FuelExpenseFormTankChargeItem",
  props: {
    charge: {
      type: Object,
      required: true
    },
    vehicles: {
      type: Array,
      default: () => []
    },
    staffList: {
      type: Array,
      default: () => []
    },
    hasFuelableError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localDateMenu: false
    };
  },
  computed: {
    staffOptions() {
      if (!this.charge.fuelable || !this.charge.fuelable.type) {
        return this.staffList;
      }

      const fuelableType = this.charge.fuelable.type;

      if (
        fuelableType === "OwnMachineExpense" ||
        fuelableType === "StaffMachineExpense" ||
        fuelableType === "RentedMachineExpense"
      ) {
        return this.staffList;
      } else if (fuelableType === "TankExpense") {
        return [];
      }

      return this.staffList;
    }
  },
  methods: {
    updateDate(value) {
      this.localDateMenu = false;
      this.$emit("update-field", "dateCharged", value);
    }
  }
};
</script>

<style scoped>
.tank-charge-row {
  background-color: #f8f9fa;
}

.charge-error-row {
  background-color: #fff5f5;
}

.tank-charge-cell {
  padding: 16px 24px !important;
}

.tank-charge-single {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  background-color: white;
  padding: 16px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-left: 8px;
}

.charge-error-row .tank-charge-single {
  border-color: #f64e60;
  background-color: #ffe2e5;
}

.charge-field {
  display: flex;
  flex-direction: column;
  min-width: 130px;
  flex: 1;
}

.charge-field-wide {
  flex: 2;
  min-width: 200px;
}

.charge-label {
  color: #666;
  font-size: 11px;
  text-transform: uppercase;
  margin-bottom: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-error {
  color: #f64e60;
}

.charge-input {
  margin-top: 0;
}

.charge-input >>> .v-input__control {
  min-height: 38px !important;
}

.field-with-error >>> .v-input__control {
  background-color: #ffe2e5 !important;
}

.field-with-error >>> .v-input__slot {
  border-color: #f64e60 !important;
}

.field-error-text {
  color: #f64e60;
  font-size: 10px;
  margin-top: 2px;
  display: block;
}

.charge-actions-field {
  flex: 0;
  min-width: auto;
}

@media (max-width: 1200px) {
  .tank-charge-single {
    flex-wrap: wrap;
    gap: 12px;
  }

  .charge-field {
    min-width: 150px;
  }

  .charge-field-wide {
    flex: 1 1 200px;
  }
}

@media (max-width: 768px) {
  .tank-charge-single {
    flex-direction: column;
    align-items: stretch;
    margin-left: 0;
  }

  .charge-field,
  .charge-field-wide {
    width: 100%;
    flex: 1;
  }
}
</style>
